import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Search, Clear } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ServiceTemplatesTable from "../components/ServiceTemplatesTable";
import ServiceTemplateFormModal from "../components/ServiceTemplateFormModal";
import ServiceTemplateDeleteConfirmationDialog from "../components/ServiceTemplateDeleteConfirmationDialog";
import Pagination from "../components/Pagination";
import { useServiceTemplatesApi } from "../hooks/useServiceTemplatesApi";
import { ServiceTemplate, PaginationMeta } from "../types/entities";
import { ServiceTemplateFormData } from "../types/forms";

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [serviceTemplates, setServiceTemplates] = useState<ServiceTemplate[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedServiceTemplate, setSelectedServiceTemplate] = useState<ServiceTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState<ServiceTemplateFormData>({
    name: "",
    description: "",
    estimated_duration: 30,
    price: "100",
  });

  const {
    error,
    fetchServiceTemplates,
    createServiceTemplate: createServiceTemplateApi,
    updateServiceTemplate: updateServiceTemplateApi,
    deleteServiceTemplate: deleteServiceTemplateApi,
  } = useServiceTemplatesApi();

  const loadServiceTemplates = async (page: number = currentPage, limit: number = itemsPerPage): Promise<void> => {
    try {
      const response = await fetchServiceTemplates({ page, limit });
      setServiceTemplates(response.data);
      setPaginationMeta(response.meta);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const createServiceTemplate = async (data?: ServiceTemplateFormData | Partial<ServiceTemplateFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to form data
      const dataToCreate = data || formData;
      await createServiceTemplateApi(dataToCreate as ServiceTemplateFormData);
      await loadServiceTemplates();
      setOpenCreateModal(false);
      resetForm();
      toast.success(t('services.serviceTemplateCreated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const updateServiceTemplate = async (changedData?: Partial<ServiceTemplateFormData>): Promise<void> => {
    if (!selectedServiceTemplate) return;

    try {
      // Use changed data if provided, otherwise use all form data
      const dataToUpdate = changedData || formData;
      await updateServiceTemplateApi(selectedServiceTemplate.id, dataToUpdate);
      await loadServiceTemplates();
      setOpenEditModal(false);
      setSelectedServiceTemplate(null);
      resetForm();
      toast.success(t('services.serviceTemplateUpdated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const deleteServiceTemplate = async (): Promise<void> => {
    if (!selectedServiceTemplate) return;

    try {
      await deleteServiceTemplateApi(selectedServiceTemplate.id);
      await loadServiceTemplates();
      setOpenDeleteDialog(false);
      setSelectedServiceTemplate(null);
      toast.success(t('services.serviceTemplateDeleted'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: "",
      description: "",
      estimated_duration: 30,
      price: "100",
    });
  };

  const handleInputChange =
    (field: keyof ServiceTemplateFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: field === 'description' && value === '' ? undefined : 
                 field === 'estimated_duration' ? 
                 parseFloat(value) || 0 : 
                 field === 'price' ?
                 value || "0" : value,
      }));
    };

  const handleEditClick = (serviceTemplate: ServiceTemplate): void => {
    setSelectedServiceTemplate(serviceTemplate);
    setFormData({
      name: serviceTemplate.name,
      description: serviceTemplate.description || "",
      estimated_duration: serviceTemplate.estimated_duration,
      price: serviceTemplate.price,
    });
    setOpenEditModal(true);
  };

  const handleDeleteClick = (serviceTemplate: ServiceTemplate): void => {
    setSelectedServiceTemplate(serviceTemplate);
    setOpenDeleteDialog(true);
  };

  const handleClearSearch = (): void => {
    setSearchTerm("");
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    loadServiceTemplates(page, itemsPerPage);
  };

  const handleLimitChange = (limit: number): void => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadServiceTemplates(1, limit);
  };

  const handleModalClose = (): void => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    resetForm();
  };

  const filteredServiceTemplates = serviceTemplates.filter((serviceTemplate) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      serviceTemplate.name.toLowerCase().includes(searchLower) ||
      (serviceTemplate.description && serviceTemplate.description.toLowerCase().includes(searchLower))
    );
  });

  useEffect(() => {
    const initializeServiceTemplates = async () => {
      try {
        await loadServiceTemplates();
      } finally {
        setInitialLoading(false);
      }
    };

    initializeServiceTemplates();
  }, []);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
  <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ mb: 0 }}>
          {t('services.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCreateModal(true)}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('services.addServiceTemplate')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {/* Always show search controls */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('services.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Show results or no results message */}
      {filteredServiceTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm 
              ? t('services.noServiceTemplatesMatching', { searchTerm }) 
              : t('services.noServiceTemplatesFound')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm 
              ? t('services.noServiceTemplatesMatchingMessage') 
              : t('services.noServiceTemplatesMessage')}
          </Typography>
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<Clear />}
            >
              {t('services.clearSearch')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ 
            minHeight: `${Math.max(filteredServiceTemplates.length * 60 + 80, 120)}px`, 
            maxHeight: '500px', 
            overflow: 'auto', 
            mb: 2 
          }}>
            <ServiceTemplatesTable
              serviceTemplates={filteredServiceTemplates}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          </Box>
          {paginationMeta && (
            <Box display="flex" justifyContent="center">
              <Pagination
                meta={paginationMeta}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </Box>
          )}
        </>
      )}

      <ServiceTemplateFormModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSubmit={createServiceTemplate}
        formData={formData}
        onInputChange={handleInputChange}
        title={t('services.addNewServiceTemplate')}
        submitButtonText={t('common.create')}
      />

      <ServiceTemplateFormModal
        open={openEditModal}
        onClose={handleModalClose}
        onSubmit={updateServiceTemplate}
        formData={formData}
        onInputChange={handleInputChange}
        title={t('services.editServiceTemplate')}
        submitButtonText={t('common.save')}
        isEdit={true}
      />

      <ServiceTemplateDeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deleteServiceTemplate}
        serviceTemplate={selectedServiceTemplate}
      />
  </Container>
);
};

export default ServicesPage;
